import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import planoService, {
  type PlanoTrabalhoAnaliseJuridica,
  type PlanoTrabalhoAnaliseJuridicaCatalogo,
  type PlanoTrabalhoAnaliseJuridicaItem,
} from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';

const STATUS_LABELS: Record<PlanoTrabalhoAnaliseJuridicaItem['statusResposta'], string> = {
  CONFORME: 'Conforme',
  NAO_CONFORME: 'Nao conforme',
  PENDENTE: 'Pendente',
  NAO_APLICAVEL: 'Nao aplicavel',
};

const ENCAMINHAMENTO_LABELS: Record<NonNullable<PlanoTrabalhoAnaliseJuridica['encaminhamentoFinal']>, string> = {
  APROVACAO: 'Aprovacao',
  APROVACAO_COM_RESSALVAS: 'Aprovacao com ressalvas',
  REJEICAO: 'Rejeicao',
  DILIGENCIA: 'Diligencia',
};

const Field: React.FC<{ label: string; value?: React.ReactNode; className?: string }> = ({ label, value, className = '' }) => (
  <div className={`rounded-md border border-slate-200 px-3 py-3 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-1 text-[13px] leading-5 text-slate-900">{value ?? 'Nao informado'}</div>
  </div>
);

const SectionTitle: React.FC<{ number: string; title: string }> = ({ number, title }) => (
  <div className="mb-3 mt-8 border-b border-slate-300 pb-2 print-keep-with-next">
    <h2 className="text-base font-bold uppercase tracking-[0.12em] text-slate-900">{number} - {title}</h2>
  </div>
);

const PlanoAnaliseJuridicaPrintPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plano, setPlano] = useState<PlanoTrabalho | null>(null);
  const [catalogo, setCatalogo] = useState<PlanoTrabalhoAnaliseJuridicaCatalogo | null>(null);
  const [analise, setAnalise] = useState<PlanoTrabalhoAnaliseJuridica | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Plano nao informado.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [planoData, catalogoData, analiseData] = await Promise.all([
          planoService.get(id),
          planoService.getAnaliseJuridicaCatalogo('TERMO_FOMENTO'),
          planoService.getAnaliseJuridica(id),
        ]);

        setPlano(planoData);
        setCatalogo(catalogoData);
        setAnalise(analiseData);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Nao foi possivel carregar o impresso da analise juridica.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    if (!loading && !error && searchParams.get('autoprint') === '1') {
      const timeout = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [loading, error, searchParams]);

  const itemMap = useMemo(() => {
    const entries = (analise?.itens || []).map((item) => [item.itemCodigo, item] as const);
    return Object.fromEntries(entries);
  }, [analise?.itens]);

  const faseMap = useMemo(() => {
    const entries = (analise?.fases || []).map((fase) => [fase.faseId, fase] as const);
    return Object.fromEntries(entries);
  }, [analise?.fases]);

  const counts = useMemo(() => {
    return (catalogo?.fases || [])
      .flatMap((fase) => fase.grupos.flatMap((grupo) => grupo.itens))
      .reduce(
        (acc, item) => {
          const status = itemMap[item.codigo]?.statusResposta;
          if (status === 'CONFORME') acc.conforme += 1;
          if (status === 'NAO_CONFORME') acc.naoConforme += 1;
          if (status === 'PENDENTE') acc.pendente += 1;
          if (status === 'NAO_APLICAVEL') acc.naoAplicavel += 1;
          if (status) acc.respondidos += 1;
          acc.total += 1;
          return acc;
        },
        { total: 0, respondidos: 0, conforme: 0, naoConforme: 0, pendente: 0, naoAplicavel: 0 }
      );
  }, [catalogo?.fases, itemMap]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 px-4 py-10 text-center text-slate-600">Carregando impresso da analise juridica...</div>;
  }

  if (error || !plano || !catalogo || !analise) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-red-700">Erro ao gerar impresso</p>
          <p className="mt-2 text-sm text-slate-600">{error || 'Analise juridica nao encontrada.'}</p>
          <button type="button" onClick={() => navigate(-1)} className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{`
        @page { size: A4 portrait; margin: 14mm; }
        @media print {
          .print-hidden { display: none !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          .print-keep-together {
            break-inside: avoid-page !important;
            page-break-inside: avoid !important;
          }
          .print-keep-with-next {
            break-after: avoid-page !important;
            page-break-after: avoid !important;
          }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="print-hidden sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Impresso da Analise Juridica</p>
            <p className="text-xs text-slate-500">Termo de Fomento</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate(-1)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700">
              Voltar
            </button>
            <button type="button" onClick={() => window.print()} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white">
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <main className="print-sheet mx-auto my-6 max-w-5xl rounded-3xl bg-white px-6 py-8 shadow-xl print:my-0 print:rounded-none print:px-0 print:py-0">
        <header className="border-b border-slate-300 pb-5">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Procuradoria Geral Municipal</p>
          <h1 className="mt-2 text-center text-2xl font-bold uppercase tracking-[0.16em] text-slate-900">Analise Juridica do Termo de Fomento</h1>

          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-4 print:grid-cols-4">
            <Field label="Plano de Trabalho" value={plano.titulo || 'Nao informado'} className="sm:col-span-2 print:col-span-2" />
            <Field label="Protocolo" value={plano.numeroProtocolo || 'Sem protocolo'} />
            <Field label="Etapa Atual" value={plano.etapaAtual || 'Nao informada'} />
            <Field label="Modalidade" value={plano.modalidade || 'Nao informada'} />
            <Field label="Checklist" value={analise.checklistVersao || catalogo.versao} />
            <Field label="Atualizado em" value={analise.dataAtualizacao ? new Date(analise.dataAtualizacao).toLocaleString('pt-BR') : 'Nao informado'} />
            <Field label="Encaminhamento Final" value={analise.encaminhamentoFinal ? ENCAMINHAMENTO_LABELS[analise.encaminhamentoFinal] : 'Nao definido'} className="sm:col-span-2 print:col-span-2" />
          </div>
        </header>

        <SectionTitle number="1" title="Resumo da Analise" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 print:grid-cols-5">
          <Field label="Itens" value={counts.total} />
          <Field label="Respondidos" value={counts.respondidos} />
          <Field label="Conformes" value={counts.conforme} />
          <Field label="Nao Conformes" value={counts.naoConforme} />
          <Field label="Pendentes" value={counts.pendente} />
        </div>

        <SectionTitle number="2" title="Checklist Respondido" />
        <div className="space-y-6">
          {catalogo.fases.map((fase) => (
            <section key={fase.id} className="print-keep-together rounded-2xl border border-slate-200 p-4">
              <div className="border-b border-slate-200 pb-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Fase {fase.ordem}</p>
                <h2 className="mt-1 text-lg font-bold text-slate-900">{fase.titulo}</h2>
                <p className="mt-1 text-sm text-slate-600">{fase.descricao}</p>
              </div>

              <div className="mt-4 space-y-4">
                {fase.grupos.map((grupo) => (
                  <div key={grupo.id}>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">{grupo.label}</p>
                    <div className="space-y-2">
                      {grupo.itens.map((item) => {
                        const resposta = itemMap[item.codigo];
                        return (
                          <div key={item.codigo} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{item.descricao}</p>
                                <p className="mt-1 text-xs text-slate-500">{item.fundamentoLegal}</p>
                              </div>
                              <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                                {resposta?.statusResposta ? STATUS_LABELS[resposta.statusResposta] : 'Sem resposta'}
                              </span>
                            </div>
                            {resposta?.observacaoItem && (
                              <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                                <strong>Observacao:</strong> {resposta.observacaoItem}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Observacoes da fase</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{faseMap[fase.id]?.observacaoFase || 'Sem observacoes registradas.'}</p>
              </div>
            </section>
          ))}
        </div>

        <SectionTitle number="3" title="Conclusao Juridica" />
        <div className="space-y-3">
          <Field
            label="Encaminhamento Final"
            value={analise.encaminhamentoFinal ? ENCAMINHAMENTO_LABELS[analise.encaminhamentoFinal] : 'Nao definido'}
          />
          <Field
            label="Fundamentacao Juridica"
            value={<p className="whitespace-pre-wrap">{analise.fundamentacao || 'Nao informada.'}</p>}
          />
          <Field
            label="Parecer Consolidado"
            value={<p className="whitespace-pre-wrap">{analise.parecerConsolidado || 'Nao informado.'}</p>}
          />
        </div>
      </main>
    </div>
  );
};

export default PlanoAnaliseJuridicaPrintPage;
